<script setup>
import { useResizeObserver, useThrottleFn } from '@vueuse/core';
import { WdsPillFloating } from '@wds/components';

const props = defineProps({
  fadeBoundaries: {
    type: Boolean,
    required: false,
    default: true,
  },
  fadeBoundariesColor: {
    type: String,
    required: false,
    default: 'white',
  },
  itemCount: {
    type: Number,
    default: null,
  },
});

// ### EMITTED EVENTS
const emit = defineEmits(['moveItem']);

// DATA
const galleryList = ref(null);
const activeGalleryItem = ref(0);
const galleryNavButtonsBlocked = ref({
  left: true,
  right: false,
});
const hasGalleryOverflow = ref(false);

const MAX_ITEMS_WITHOUT_NAV_ARROWS = 4;
const RESIZE_THROTTLE_MS = 100;

let galleryListElement = null;

// ### COMPUTED
const fadeBoundariesStyle = computed(() =>
  props.fadeBoundaries
    ? { '--fade-boundaries-color': `var(--wds-color-${props.fadeBoundariesColor})` }
    : null,
);

const galleryItemCount = computed(() => {
  if (Number.isInteger(props.itemCount) && props.itemCount >= 0) {
    return props.itemCount;
  }

  return galleryList.value?.children?.length ?? 0;
});

const shouldHideNavArrows = computed(() => {
  return galleryItemCount.value <= MAX_ITEMS_WITHOUT_NAV_ARROWS && !hasGalleryOverflow.value;
});

// ### METHODS
function getGalleryItemFullWidth(galleryItem) {
  if (!galleryItem) {
    return null;
  }

  const galleryItemStyle = window.getComputedStyle(galleryItem);
  return (
    galleryItem.getBoundingClientRect().width
    + Number.parseFloat(galleryItemStyle.marginLeft)
    + Number.parseFloat(galleryItemStyle.marginRight)
  );
}

function checkGalleryOverflow() {
  const element = galleryList.value;

  if (!element) {
    hasGalleryOverflow.value = false;
    return;
  }

  hasGalleryOverflow.value = element.scrollWidth > element.offsetWidth;
}

const throttledCheckGalleryOverflow = useThrottleFn(checkGalleryOverflow, RESIZE_THROTTLE_MS);

useResizeObserver(galleryList, throttledCheckGalleryOverflow);

function handleGalleryScroll(e) {
  const galleryListRef = toValue(galleryList);
  if (!galleryListRef) {
    return;
  }

  const galleryFirstCard = galleryListRef.firstElementChild;
  if (!galleryFirstCard) {
    return;
  }

  const scrollLeft = e.currentTarget.scrollLeft;
  const itemWidth = getGalleryItemFullWidth(galleryFirstCard);
  if (itemWidth) {
    activeGalleryItem.value = Math.round(scrollLeft / itemWidth);

    galleryNavButtonsBlocked.value.left = scrollLeft <= itemWidth;
    galleryNavButtonsBlocked.value.right
      = scrollLeft >= galleryListRef.scrollWidth - galleryListRef.offsetWidth;
  }
}

function moveGalleryItem(direction) {
  const validDirection = [1, -1].includes(direction) ? direction : 1;
  const galleryListRef = toValue(galleryList);

  if (!galleryListRef) {
    return;
  }

  const galleryCardsCount = galleryListRef.children.length;

  if (
    (validDirection < 0 && activeGalleryItem.value <= 0)
    || (validDirection > 0 && activeGalleryItem.value >= galleryCardsCount - 1)
  ) {
    return;
  }

  activeGalleryItem.value += validDirection;
  moveGalleryToActive();
  emit('moveItem');
}

function moveGalleryToActive() {
  const galleryListRef = toValue(galleryList);
  if (!galleryListRef) {
    return;
  }

  const galleryCards = galleryListRef.children;
  const nextCard = galleryCards[activeGalleryItem.value];
  if (nextCard) {
    nextCard.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  }
}

onMounted(() => {
  galleryListElement = galleryList.value;
  galleryListElement?.addEventListener('scrollend', handleGalleryScroll, false);
  checkGalleryOverflow();
});

onUnmounted(() => {
  galleryListElement?.removeEventListener('scrollend', handleGalleryScroll, false);
  galleryListElement = null;
});
</script>

<template>
  <div
    class="carousel-wrapper"
    :class="{ 'fade-boundaries': fadeBoundaries }"
    :style="fadeBoundariesStyle"
  >
    <div
      class="carousel-nav-buttons"
      :class="{
        'carousel-nav-buttons-hidden': shouldHideNavArrows,
      }"
    >
      <WdsPillFloating
        icon-start="arrow-left"
        aria-label="left arrow"
        :disabled="galleryNavButtonsBlocked.left"
        @click="moveGalleryItem(-1)"
      />
      <WdsPillFloating
        icon-start="arrow-right"
        aria-label="right arrow"
        :disabled="galleryNavButtonsBlocked.right"
        @click="moveGalleryItem(1)"
      />
    </div>
    <div
      ref="galleryList"
      class="carousel-gallery-list"
    >
      <slot />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.carousel-wrapper {
  position: relative;

  .carousel-nav-buttons {
    display: none;
  }

  .carousel-gallery-list {
    display: flex;
    gap: $wds-spacing-m;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    position: relative;
    padding-top: $wds-spacing-s;
    padding-bottom: $wds-spacing-m;

    @include no-scrollbar;

    > * {
      scroll-snap-align: start;
    }
  }

  &.fade-boundaries {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -$wds-spacing-m;
      width: $wds-spacing-m;
      bottom: $wds-spacing-m;
      background: linear-gradient(to right, var(--fade-boundaries-color), transparent);
      z-index: 1;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: -$wds-spacing-m;
      width: $wds-spacing-m;
      bottom: $wds-spacing-m;
      background: linear-gradient(to left, var(--fade-boundaries-color), transparent);
      z-index: 1;
    }

    .carousel-gallery-list {
      margin-inline: -$wds-spacing-m;
      padding-inline: $wds-spacing-m;

      > * {
        scroll-margin-inline: $wds-spacing-m;
      }
    }
  }

  @include desktop {
    .carousel-nav-buttons {
      position: absolute;
      top: -$wds-spacing-xxl;
      right: 0;
      display: flex;
      gap: $wds-spacing-xs;

      &-hidden {
        display: none;
      }
    }
  }
}
</style>
